import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authed, object = authed
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    api
      .get("/auth/me")
      .then((res) => mounted && setUser(res.data))
      .catch(() => mounted && setUser(false));
    return () => {
      mounted = false;
    };
  }, []);

  async function login(email, password) {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.two_factor_required) {
        // Defer setUser — caller handles the 2FA prompt
        return { two_factor_required: true, challenge_token: data.challenge_token };
      }
      if (data.access_token) sessionStorage.setItem("nyk_token", data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  async function loginVerify2fa(challenge_token, code) {
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/login-verify",
        { challenge_token, code });
      if (data.access_token) sessionStorage.setItem("nyk_token", data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  async function register(payload) {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data.access_token) sessionStorage.setItem("nyk_token", data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (_) {}
    sessionStorage.removeItem("nyk_token");
    setUser(false);
  }

  return (
    <AuthContext.Provider value={{ user, error, login, loginVerify2fa, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
